import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { getAuthHeadersFromUserData } from '../shared/utils/auth-headers.util';

export interface CardDetails {
  brand: string;
  last4: string;
  exp_month: number;
  exp_year: number;
}

export interface BillingAddress {
  line1: string;
  country: string | null;
}

export interface BillingDetails {
  name: string;
  email: string | null;
  phone: string | null;
  address: BillingAddress;
}

export interface PaymentTransactionAddon {
  addon_id?: string;
  addon_name: string;
  add_on_count?: number;
  price: number | string;
}

export interface PaymentTransactionHistory {
  order_id: string;
  amount: string;
  /** Paid total for this transaction when present; used to reconcile package vs combined `amount`. */
  final_price?: string | number;
  /**
   * What THIS payment actually collected (`payments.amount`), as opposed to `amount`/`final_price`
   * which come from the order and are frozen at signup. A membership on a discount ladder bills a
   * different amount every renewal, so only this field distinguishes them. Absent on responses
   * from older backends.
   */
  payment_amount?: string | number | null;
  /** Amount refunded against this specific payment, when any. */
  payment_refunded_amount?: string | number | null;
  currency: string;
  payment_status: string;
  payment_date: string;
  /** Present for card payments; may be null for cash and some gateways. */
  payment_intent_id: string | null;
  package_id: string;
  voucher_code?: string;
  wash_type: string;
  discount: string;
  /** Human-readable name for the applied discount (e.g. "Senior Citizen"). */
  discount_name?: string;
  /** Line-item add-ons returned by transaction history API. */
  addons?: PaymentTransactionAddon[];
  package_name: string | null;
  prepaid_wash_book_name?: string | null;
  /** Package receipt promo text when enabled; omit/null means no promo box. */
  promo_message?: string | null;
  payment_method?: string;
  /**
   * Card-on-file / setup events (e.g. payment_method_added), and the card this payment was
   * charged on with its billing contact (CWS-1508; `billing_details` absent on older rows).
   */
  payload?: {
    event?: string;
    brand?: string;
    last4?: string;
    display?: string;
    billing_details?: BillingDetails | null;
  } | null;
}

const TX_RECONCILE_TOL = 0.02;

/**
 * The amount this one payment actually collected, or null when the backend did not supply it.
 *
 * One order can produce many payments — a membership renews monthly, and on a discount ladder
 * each renewal bills a different amount — while `amount` (order list_price) and `final_price`
 * are fixed at signup. Reading only those made every renewal render as the same payment.
 *
 * Returns null rather than 0 for a missing value so callers can fall back to the old
 * order-derived arithmetic: a $0 payment is a real outcome (a fully discounted invoice) and must
 * stay distinguishable from "this response has no per-payment amount".
 */
export function resolvePaymentTransactionPaidAmount(
  th: Pick<PaymentTransactionHistory, 'payment_amount'>
): number | null {
  const raw = th.payment_amount;
  if (raw === null || raw === undefined || String(raw).trim() === '') {
    return null;
  }
  const value = Number(raw);
  return isFinite(value) ? value : null;
}

/**
 * The discount attributable to THIS payment, so an itemised receipt reconciles with its total.
 *
 * `discount` is the order's discount, fixed at signup. On a membership whose renewals step down
 * a discount ladder, a later renewal may be discounted by a different amount — or not at all —
 * while that column still reads the signup figure. Deriving it from what was actually collected
 * keeps subtotal − discount === paid on every line-item breakdown.
 *
 * Falls back to the order's own discount when the backend sends no per-payment amount, which is
 * exactly the previous behaviour.
 */
export function resolvePaymentTransactionDiscount(
  th: PaymentTransactionHistory,
  subtotalBeforeDiscount: number
): number {
  const paid = resolvePaymentTransactionPaidAmount(th);
  if (paid !== null && isFinite(subtotalBeforeDiscount)) {
    return Math.max(0, subtotalBeforeDiscount - paid);
  }
  return Math.max(0, Number(th.discount || 0));
}

/** Sum of priced add-on lines (price × count) for transaction history rows. */
export function sumPaymentTransactionAddonAmount(
  th: Pick<PaymentTransactionHistory, 'addons'>
): number {
  const addonRows = Array.isArray(th.addons) ? th.addons : [];
  let addonSum = 0;
  for (const a of addonRows) {
    const count = Math.max(1, Number(a.add_on_count || 1));
    const p = Number(a.price ?? 0);
    if (isFinite(p) && isFinite(count)) {
      addonSum += p * count;
    }
  }
  return addonSum;
}

/**
 * Whether `amount` already represents package + add-ons (vs package/wash line only with add-ons listed separately).
 * When `final_price` + discount + family reconcile, we prefer that over the loose `amount >= addonSum` heuristic
 * (which misclassified e.g. amount=2, add-on=1 as "inclusive" and showed package as $1).
 *
 * Also: when `amount` matches that reconciliation but the sum of add-on lines is **greater** than `amount`,
 * `amount` is the wash/package line only (e.g. custom list price) — not a combined total, so subtracting
 * add-ons from it would incorrectly show $0 for the package.
 */
export function isPaymentTransactionAmountInclusiveOfAddons(
  th: PaymentTransactionHistory
): boolean {
  const amountNum = Number(th.amount || 0);
  const addonSum = sumPaymentTransactionAddonAmount(th);
  if (addonSum <= TX_RECONCILE_TOL) {
    return false;
  }

  const discountNum = Number(th.discount || 0);
  const fpRaw = th.final_price ?? (th as any as Record<string, any>)['finalPrice'];
  const fp = fpRaw != null && String(fpRaw).trim() !== '' ? Number(fpRaw) : NaN;
  const preDisc = isFinite(fp) ? fp + discountNum : NaN;

  if (isFinite(preDisc)) {
    // `amount` equals paid+discount reconciliation, but add-on lines sum to more than `amount`:
    // `amount` is the wash/package line only, not package+add-ons combined.
    if (
      Math.abs(amountNum - preDisc) < TX_RECONCILE_TOL &&
      addonSum > amountNum + TX_RECONCILE_TOL
    ) {
      return false;
    }
    if (Math.abs(amountNum - preDisc) < TX_RECONCILE_TOL) {
      return true;
    }
    if (Math.abs(amountNum + addonSum - preDisc) < TX_RECONCILE_TOL) {
      return false;
    }
  }

  return addonSum > 0 && amountNum + TX_RECONCILE_TOL >= addonSum;
}

export type TransactionHistoryResponse = Record<
  string,
  | PaymentTransactionHistory
  | CardDetails
  | BillingDetails
  | PaymentTransactionHistory[]
  | string
  | null
  | undefined
> & {
  transaction_history?: PaymentTransactionHistory[];
  card_details?: CardDetails | null;
  billing_details?: BillingDetails | null;
  original_order_transaction_history?: PaymentTransactionHistory[];
  original_order_id?: string;
  original_order_number?: string;
  original_order_card_details?: CardDetails | null;
  original_order_billing_details?: BillingDetails | null;
};

export interface ParsedTransactionHistoryPayload {
  transactions: PaymentTransactionHistory[];
  cardDetails: CardDetails | null;
  billingDetails: BillingDetails | null;
  originalOrderTransactions: PaymentTransactionHistory[];
  originalOrderId: string | null;
  originalOrderNumber: string | null;
  originalOrderCardDetails: CardDetails | null;
  originalOrderBillingDetails: BillingDetails | null;
}

const TRANSACTION_HISTORY_META_KEYS = new Set([
  'card_details',
  'billing_details',
  'transaction_history',
  'original_order_transaction_history',
  'original_order_id',
  'original_order_number',
  'original_order_card_details',
  'original_order_billing_details',
]);

function parseCardDetails(value: any): CardDetails | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as CardDetails)
    : null;
}

function parseBillingDetails(value: any): BillingDetails | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as BillingDetails)
    : null;
}

function parseOriginalOrderFields(raw: Record<string, any>) {
  const originalHistoryRaw = raw['original_order_transaction_history'];
  const originalOrderTransactions = Array.isArray(originalHistoryRaw)
    ? (originalHistoryRaw.filter(
        row => row && typeof row === 'object'
      ) as PaymentTransactionHistory[])
    : [];

  return {
    originalOrderTransactions,
    originalOrderId: typeof raw['original_order_id'] === 'string' ? raw['original_order_id'] : null,
    originalOrderNumber:
      typeof raw['original_order_number'] === 'string' ? raw['original_order_number'] : null,
    originalOrderCardDetails: parseCardDetails(raw['original_order_card_details']),
    originalOrderBillingDetails: parseBillingDetails(raw['original_order_billing_details']),
  };
}

/** Normalize GET /payment/transaction-history payload (array or legacy numeric-key map). */
export function parseTransactionHistoryResponse(
  th: TransactionHistoryResponse | null | undefined
): ParsedTransactionHistoryPayload {
  const empty: ParsedTransactionHistoryPayload = {
    transactions: [],
    cardDetails: null,
    billingDetails: null,
    originalOrderTransactions: [],
    originalOrderId: null,
    originalOrderNumber: null,
    originalOrderCardDetails: null,
    originalOrderBillingDetails: null,
  };
  if (!th || typeof th !== 'object') {
    return empty;
  }

  const raw = th as Record<string, any>;
  const cardDetails = parseCardDetails(raw['card_details']);
  const billingDetails = parseBillingDetails(raw['billing_details']);
  const originalOrder = parseOriginalOrderFields(raw);

  const fromArray = raw['transaction_history'];
  if (Array.isArray(fromArray)) {
    return {
      transactions: fromArray.filter(
        row => row && typeof row === 'object'
      ) as PaymentTransactionHistory[],
      cardDetails,
      billingDetails,
      ...originalOrder,
    };
  }

  const transactions: PaymentTransactionHistory[] = [];
  for (const key of Object.keys(raw)) {
    if (TRANSACTION_HISTORY_META_KEYS.has(key)) {
      continue;
    }
    if (!isNaN(Number(key))) {
      const row = raw[key];
      if (row && typeof row === 'object' && !Array.isArray(row)) {
        transactions.push(row as PaymentTransactionHistory);
      }
    }
  }
  return { transactions, cardDetails, billingDetails, ...originalOrder };
}

export interface CreatePaymentLinkRequest {
  order_id: string;
  phone_number: string;
  email?: string;
  source?: string;
}

export interface CreatePaymentLinkResponse {
  status: boolean;
  status_code: number;
  message: string;
  paymentLink: {
    success: boolean;
    url: string;
  };
}

export interface CreatePaymentIntentResponse {
  status: boolean;
  status_code: number;
  message: string;
  data: {
    payment_intent_id: string;
    client_secret: string;
    /** Stripe intent status (`requires_action`, `succeeded`, …). */
    status?: string;
    stripe_account_id?: string;
    /** Subscription-first membership billing: the intent pays the first invoice. */
    subscription_id?: string;
    stripe_subscription_id?: string;
    subscription_status?: string;
    /** `pending` until the Stripe webhook activates the membership. */
    membership_status?: string;
    billing_flow?: string;
    /** SCA/3DS: the client must confirm the intent before the backend can finish. */
    requires_action?: boolean;
    next_action_type?: string;
  };
}

/**
 * Service for handling payment-related operations against the backend API: creating and confirming payment intents, performing card payments, generating payment links, and fetching transaction/intent status.
 * @component
 * @selector app-payment-service
 * @example
 *   // Inject the service into a component or another service via Angular DI
 *   constructor(private paymentService: PaymentService) {}
 * @input none - This is an injectable service (providedIn: 'root') and does not expose Angular @Input properties.
 * @output none - The service does not emit Angular @Output events; it returns RxJS Observables for callers to subscribe to.
 *
 * Key methods:
 * - confirmPaymentIntent(paymentIntentId: string, paymentMethodId: string): Observable<any>
 *   Confirms a payment intent on the backend using the provided payment intent ID and payment method ID.
 *
 * - getPaymentIntentStatus(paymentIntentId: string): Observable<any>
 *   Retrieves the current status of a payment intent.
 *
 * - getTransactionHistory(orderId: string): Observable<{status: boolean, status_code: number, message: string, transactionHistory: TransactionHistoryResponse}>
 *   Fetches the transaction history for a given order.
 *
 * - createPaymentLink(request: CreatePaymentLinkRequest): Observable<CreatePaymentLinkResponse>
 *   Creates a payment link for an order. If a source is provided on the request it is sent as a query parameter and omitted from the request body.
 *
 * - createPaymentIntent(orderId: string, paymentMethodId?: string): Observable<CreatePaymentIntentResponse>
 *   Creates a payment intent for an order. Optionally accepts a payment_method_id so the backend can attach the method to the intent.
 *
 * - confirmCardPayment(request: { order_id: string; payment_method_id?: string; card?: { number: string; exp_month: number; exp_year: number; cvc: string; address: string; }; }): Observable<any>
 *   Confirms a card payment by sending card details or an attached payment method to the backend.
 *
 * Other details:
 * - The service is provided in root and uses HttpClient to communicate with the backend API whose base URL is read from environment configuration.
 * - Authentication headers are attached to requests via a private getHeaders() helper which obtains auth headers from user data.
 * - All network methods return RxJS Observables; callers should subscribe or use async pipes to consume responses.
 * - Lifecycle hooks: none (service-level initialization is done in the constructor; no Angular lifecycle hooks apply).
 */
@Injectable({
  providedIn: 'root',
})
export class PaymentService {
  private readonly apiUrl = `${environment.baseUrl}/${environment.apiVersion}`;

  constructor(private http: HttpClient) {}

  private getHeaders(): HttpHeaders {
    return getAuthHeadersFromUserData();
  }

  /**
   * Confirm a payment intent
   */
  confirmPaymentIntent(paymentIntentId: string, paymentMethodId: string): Observable<any> {
    return this.http.post(
      `${this.apiUrl}/payments/confirm`,
      {
        paymentIntentId,
        paymentMethodId,
      },
      {
        headers: this.getHeaders(),
      }
    );
  }

  /**
   * Get payment intent status
   */
  getPaymentIntentStatus(paymentIntentId: string): Observable<any> {
    return this.http.get(`${this.apiUrl}/payments/intent/${paymentIntentId}`, {
      headers: this.getHeaders(),
    });
  }

  /**
   * Get transaction history for an order
   */
  getTransactionHistory(orderId: string): Observable<{
    status: boolean;
    status_code: number;
    message: string;
    transactionHistory: TransactionHistoryResponse;
  }> {
    return this.http.get<{
      status: boolean;
      status_code: number;
      message: string;
      transactionHistory: TransactionHistoryResponse;
    }>(`${this.apiUrl}/payment/transaction-history/${orderId}`, {
      headers: this.getHeaders(),
    });
  }

  /**
   * Create payment link for an order
   */
  createPaymentLink(request: CreatePaymentLinkRequest): Observable<CreatePaymentLinkResponse> {
    // Build URL with source query parameter if provided
    let url = `${this.apiUrl}/payment/create-payment-link`;
    const { source } = request;

    if (source) {
      url += `?source=${source}`;
    }

    // Remove source from request body as it's sent as query parameter
    const { source: _, ...requestBody } = request;

    return this.http.post<CreatePaymentLinkResponse>(url, requestBody, {
      headers: this.getHeaders(),
    });
  }

  /**
   * Create a payment intent for an order (first step when user selects card payment).
   * POST order/create-payment-intent/{{order_id}}
   * Optionally pass a payment_method_id so the backend can attach the PM to the intent.
   */
  createPaymentIntent(
    orderId: string,
    paymentMethodId?: string
  ): Observable<CreatePaymentIntentResponse> {
    // When second arg is provided, always send it in payload (so add-order/order-details/notification send payment_method_id)
    const body: Record<string, string> =
      paymentMethodId !== undefined ? { payment_method_id: paymentMethodId } : {};
    return this.http.post<CreatePaymentIntentResponse>(
      `${this.apiUrl}/order/create-payment-intent/${orderId}`,
      body,
      { headers: this.getHeaders() }
    );
  }

  /**
   * Confirm card payment
   */
  confirmCardPayment(request: {
    order_id: string;
    payment_method_id?: string;
    card?: {
      number: string;
      exp_month: number;
      exp_year: number;
      cvc: string;
      address: string;
    };
  }): Observable<any> {
    return this.http.post(`${this.apiUrl}/payment/card-payment`, request, {
      headers: this.getHeaders(),
    });
  }
}
